/**
 * Resumable media-capture uploader (media-capture PR 3).
 *
 * Sends ONE original, full size (no client-side compression — the server keeps
 * the original and derives the web copy), as a multipart upload:
 *
 *   POST /media-capture/uploads            → item + part_size + first part URLs
 *   PUT  part 1..N                          (≤ `concurrency` in flight, each retried
 *                                            `maxRetries`× with exponential backoff)
 *   POST /media-capture/uploads/:id/parts   → more part URLs, in batches of ≤ 50
 *   POST /media-capture/uploads/:id/complete
 *
 * Transport (decided by the server per storage driver):
 *  - `presigned` — PUT the raw slice straight to the URL with NO auth header
 *    (the URL is the credential). XHR so the bar moves within a part.
 *  - `api`       — PUT to the API-relative URL with the bearer token and
 *    `application/octet-stream` (local-disk driver; dev/CI only).
 *
 * Resume: the item id is remembered (sessionStorage, keyed by the entity,
 * placement and the file's name/size/lastModified). Starting the same file
 * again — after a failed part, a dropped connection or a page reload where the
 * agent re-picks the file — asks `GET /uploads/:itemId` which parts the server
 * already holds and only sends the missing ones. Abort = `DELETE /uploads/:id`.
 */
import type {
  CreateUploadInput,
  CreateUploadResult,
  MediaEntityType,
  MediaItem,
  MediaKind,
  UploadInstructions,
  UploadStatus,
} from "@/repositories/mediaCapture";

/** The server endpoints the uploader needs (the repository satisfies it). */
export interface MediaUploadTransport {
  createUpload(input: CreateUploadInput): Promise<CreateUploadResult>;
  getUploadStatus(itemId: string): Promise<UploadStatus>;
  presignParts(itemId: string, partNumbers: number[]): Promise<UploadInstructions>;
  putPartViaApi(url: string, body: Blob): Promise<void>;
  completeUpload(itemId: string): Promise<MediaItem>;
  abortUpload(itemId: string): Promise<void>;
}

/** PUT a part to a presigned URL (no auth). `onProgress` gets bytes sent so far. */
export type PresignedPut = (
  url: string,
  body: Blob,
  opts: { signal: AbortSignal; onProgress?: (loaded: number) => void },
) => Promise<void>;

/** Minimal key/value store for resume state (sessionStorage by default). */
export interface ResumeStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
  keys(): string[];
}

export interface UploadPlacement {
  entity_type: MediaEntityType;
  entity_id: string;
  session_id?: string;
  kind: MediaKind;
  category_key?: string;
  slot_key?: string;
}

export type UploadPhase = "preparing" | "uploading" | "completing" | "done" | "error" | "aborted";

export interface UploadProgress {
  phase: UploadPhase;
  loaded: number;
  total: number;
  /** 0–100, integer. */
  percent: number;
  itemId: string | null;
}

export interface MediaUploadOptions {
  transport: MediaUploadTransport;
  putPresigned?: PresignedPut;
  store?: ResumeStore | null;
  onProgress?: (progress: UploadProgress) => void;
  /** Parts in flight at once. Default 3. */
  concurrency?: number;
  /** Retries AFTER the first attempt of a part. Default 3. */
  maxRetries?: number;
  /** Backoff base: attempt k waits base × 2^k ms. Default 500. */
  backoffMs?: number;
  sleep?: (ms: number) => Promise<void>;
}

export interface MediaUploadHandle {
  promise: Promise<MediaItem>;
  abort(): Promise<void>;
  readonly itemId: string | null;
}

export class MediaUploadAbortedError extends Error {
  constructor() {
    super("Upload aborted");
    this.name = "MediaUploadAbortedError";
  }
}

export class PartUploadError extends Error {
  readonly status: number;
  constructor(status: number) {
    super(`Part upload failed (${status || "network"})`);
    this.name = "PartUploadError";
    this.status = status;
  }
}

/** Max part numbers per `POST uploads/:id/parts` (server MAX_PART_URLS_PER_REQUEST). */
export const PART_URL_BATCH = 50;
export const DEFAULT_CONCURRENCY = 3;
export const DEFAULT_MAX_RETRIES = 3;
/** Above this size a video upload gets the "use Wi-Fi" note. */
export const LARGE_UPLOAD_BYTES = 100 * 1024 * 1024;

const STORE_PREFIX = "dallali-media-upload:";

interface StoredUpload {
  itemId: string;
  name: string;
  size: number;
  lastModified: number;
  entityId: string;
  categoryKey: string | null;
  slotKey: string | null;
}

/** Stable resume key for one file in one placement. */
export function resumeKey(
  file: Pick<File, "name" | "size" | "lastModified">,
  placement: Pick<UploadPlacement, "entity_type" | "entity_id" | "category_key" | "slot_key">,
): string {
  return [
    STORE_PREFIX + placement.entity_type,
    placement.entity_id,
    placement.category_key ?? "",
    placement.slot_key ?? "",
    file.name,
    file.size,
    file.lastModified,
  ].join(":");
}

/** sessionStorage-backed store; every access is guarded (private mode, blocked storage). */
export function sessionResumeStore(): ResumeStore | null {
  let storage: Storage;
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    storage = window.sessionStorage;
  } catch {
    return null;
  }
  return {
    get: (key) => {
      try {
        return storage.getItem(key);
      } catch {
        return null;
      }
    },
    set: (key, value) => {
      try {
        storage.setItem(key, value);
      } catch {
        /* quota / blocked — resume just won't be available */
      }
    },
    remove: (key) => {
      try {
        storage.removeItem(key);
      } catch {
        /* ignore */
      }
    },
    keys: () => {
      try {
        return Array.from({ length: storage.length }, (_, i) => storage.key(i)).filter(
          (k): k is string => !!k,
        );
      } catch {
        return [];
      }
    },
  };
}

function readStored(store: ResumeStore | null, key: string): StoredUpload | null {
  if (!store) return null;
  try {
    const raw = store.get(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredUpload;
    return typeof parsed?.itemId === "string" ? parsed : null;
  } catch {
    return null;
  }
}

/** Server item id of a stored (interrupted) upload, or null. */
export function storedUploadItemId(store: ResumeStore | null, key: string): string | null {
  return readStored(store, key)?.itemId ?? null;
}

/** Interrupted uploads of an entity (to tell the agent which files to re-pick). */
export function listPendingUploads(
  store: ResumeStore | null,
  entityId: string,
): { key: string; name: string; size: number; categoryKey: string | null; slotKey: string | null }[] {
  if (!store) return [];
  return store
    .keys()
    .filter((k) => k.startsWith(STORE_PREFIX))
    .map((key) => ({ key, stored: readStored(store, key) }))
    .filter((e): e is { key: string; stored: StoredUpload } => e.stored?.entityId === entityId)
    .map(({ key, stored }) => ({
      key,
      name: stored.name,
      size: stored.size,
      categoryKey: stored.categoryKey,
      slotKey: stored.slotKey,
    }));
}

const EXT_MIME: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
  mp4: "video/mp4",
  mov: "video/quicktime",
  pdf: "application/pdf",
  glb: "model/gltf-binary",
  gltf: "model/gltf+json",
  usdz: "model/vnd.usdz+zip",
  obj: "model/obj",
  zip: "application/zip",
};

/** The file's MIME type; falls back to the extension when the browser leaves it empty. */
export function mimeTypeForFile(file: Pick<File, "name" | "type">): string {
  if (file.type) return file.type.toLowerCase();
  const ext = file.name.includes(".") ? file.name.slice(file.name.lastIndexOf(".") + 1) : "";
  return EXT_MIME[ext.toLowerCase()] ?? "application/octet-stream";
}

/** Upload kind for a file placed in a category (walkthrough → video, plan → plan, …). */
export function mediaKindForFile(file: Pick<File, "name" | "type">, categoryKey: string): MediaKind {
  if (categoryKey === "three_d") return "three_d";
  const mime = mimeTypeForFile(file);
  if (mime.startsWith("video/")) return "video";
  if (categoryKey === "plan") return "plan";
  return "photo";
}

/** XHR PUT of a raw part to a presigned URL — no Authorization header. */
export const xhrPresignedPut: PresignedPut = (url, body, { signal, onProgress }) =>
  new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new MediaUploadAbortedError());
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.upload.onprogress = (e) => onProgress?.(e.loaded);
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new PartUploadError(xhr.status));
    xhr.onerror = () => reject(new PartUploadError(0));
    xhr.onabort = () => reject(new MediaUploadAbortedError());
    signal.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(body);
  });

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

function isNotFound(err: unknown): boolean {
  const status = (err as { status?: unknown } | null)?.status;
  return typeof status === "number" && status === 404;
}

/**
 * Start (or resume) the upload of `file` into `placement`. Returns a handle
 * whose promise resolves with the completed item (upload_state ready/failed —
 * a failed web copy, e.g. `heic_unsupported`, is still a stored original).
 */
export function startMediaUpload(
  file: File,
  placement: UploadPlacement,
  options: MediaUploadOptions,
): MediaUploadHandle {
  const {
    transport,
    putPresigned = xhrPresignedPut,
    store = null,
    onProgress,
    concurrency = DEFAULT_CONCURRENCY,
    maxRetries = DEFAULT_MAX_RETRIES,
    backoffMs = 500,
    sleep = defaultSleep,
  } = options;
  const key = resumeKey(file, placement);
  const controller = new AbortController();
  const total = file.size;
  let itemId: string | null = null;
  let aborted = false;
  let completedBytes = 0;
  const inFlight = new Map<number, number>();

  const emit = (phase: UploadPhase) => {
    let loaded = completedBytes;
    inFlight.forEach((n) => (loaded += n));
    loaded = Math.min(loaded, total);
    const percent =
      phase === "done" ? 100 : total > 0 ? Math.min(99, Math.floor((loaded / total) * 100)) : 0;
    onProgress?.({ phase, loaded, total, percent, itemId });
  };

  const checkAborted = () => {
    if (aborted) throw new MediaUploadAbortedError();
  };

  const run = async (): Promise<MediaItem> => {
    emit("preparing");
    let partSize = 0;
    let partCount = 0;
    let mode: "presigned" | "api" | null = null;
    const urls = new Map<number, string>();
    let todo: number[] = [];

    const stored = readStored(store, key);
    if (stored) {
      let status: UploadStatus | null = null;
      try {
        status = await transport.getUploadStatus(stored.itemId);
      } catch (err) {
        if (!isNotFound(err)) throw err;
        store?.remove(key);
      }
      if (status) {
        itemId = stored.itemId;
        if (status.upload.state !== "pending") {
          // Already assembled (a complete was lost) — complete is idempotent.
          emit("completing");
          const item = await transport.completeUpload(itemId);
          store?.remove(key);
          completedBytes = total;
          emit("done");
          return item;
        }
        partSize = status.upload.part_size;
        partCount = status.upload.part_count;
        todo = [...status.upload.missing_part_numbers];
        completedBytes = status.upload.uploaded_parts.reduce((sum, p) => sum + p.size, 0);
      }
    }

    checkAborted();
    if (!itemId) {
      const created = await transport.createUpload({
        ...placement,
        mime_type: mimeTypeForFile(file),
        bytes: file.size,
        original_name: file.name,
      });
      itemId = created.item.id;
      store?.set(
        key,
        JSON.stringify({
          itemId,
          name: file.name,
          size: file.size,
          lastModified: file.lastModified,
          entityId: placement.entity_id,
          categoryKey: placement.category_key ?? null,
          slotKey: placement.slot_key ?? null,
        } satisfies StoredUpload),
      );
      mode = created.upload.transport;
      partSize = created.upload.part_size;
      partCount = created.upload.part_count;
      created.upload.urls.forEach((u) => urls.set(u.part_number, u.url));
      todo = Array.from({ length: partCount }, (_, i) => i + 1);
      if (aborted) {
        // abort() ran while the item was being created — drop the draft now.
        store?.remove(key);
        await transport.abortUpload(itemId).catch(() => undefined);
        throw new MediaUploadAbortedError();
      }
    }
    const id = itemId;
    emit("uploading");

    const queue = [...todo];
    let refill: Promise<void> | null = null;

    const fetchUrls = async (numbers: number[]) => {
      const res = await transport.presignParts(id, numbers);
      mode = res.transport;
      res.urls.forEach((u) => urls.set(u.part_number, u.url));
    };

    /** Make sure part `n` has a URL; asks for it plus the next queued parts in one batch. */
    const ensureUrl = async (n: number) => {
      while (!urls.has(n)) {
        if (!refill) {
          const batch = [n, ...queue.filter((q) => !urls.has(q) && q !== n)].slice(0, PART_URL_BATCH);
          refill = fetchUrls(batch).finally(() => {
            refill = null;
          });
        }
        await refill;
      }
    };

    const putPart = async (n: number) => {
      const start = (n - 1) * partSize;
      const body = file.slice(start, Math.min(start + partSize, file.size));
      for (let attempt = 0; ; attempt++) {
        checkAborted();
        await ensureUrl(n);
        const url = urls.get(n)!;
        try {
          inFlight.set(n, 0);
          if (mode === "api") {
            await transport.putPartViaApi(url, body);
          } else {
            await putPresigned(url, body, {
              signal: controller.signal,
              onProgress: (loaded) => {
                inFlight.set(n, loaded);
                emit("uploading");
              },
            });
          }
          inFlight.delete(n);
          completedBytes += body.size;
          emit("uploading");
          return;
        } catch (err) {
          inFlight.delete(n);
          if (aborted || err instanceof MediaUploadAbortedError) throw new MediaUploadAbortedError();
          if (attempt >= maxRetries) throw err;
          await sleep(backoffMs * 2 ** attempt);
          // A presigned URL may have expired — sign this part again.
          if (mode === "presigned") urls.delete(n);
        }
      }
    };

    const worker = async () => {
      while (queue.length) {
        checkAborted();
        const n = queue.shift()!;
        await putPart(n);
      }
    };
    await Promise.all(
      Array.from({ length: Math.max(1, Math.min(concurrency, queue.length || 1)) }, worker),
    );

    checkAborted();
    emit("completing");
    const item = await transport.completeUpload(id);
    store?.remove(key);
    completedBytes = total;
    emit("done");
    return item;
  };

  const promise = run().catch((err: unknown) => {
    if (aborted || err instanceof MediaUploadAbortedError) {
      emit("aborted");
      throw new MediaUploadAbortedError();
    }
    // Keep the resume entry: starting the same file again picks up here.
    emit("error");
    throw err;
  });

  return {
    promise,
    get itemId() {
      return itemId;
    },
    async abort() {
      if (aborted) return;
      aborted = true;
      controller.abort();
      store?.remove(key);
      if (itemId) {
        await transport.abortUpload(itemId).catch(() => undefined);
      }
    },
  };
}
